import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SCREENS = ['signal_room', 'blueprint', 'stage', 'showcase', 'intelligence', 'analysis', 'audition'];

function relTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  const now = Date.now();
  const sec = Math.round((now - d.getTime()) / 1000);
  if (sec < 60) return 'Just now';
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
  if (sec < 172800) return 'Yesterday';
  return `${Math.floor(sec / 86400)}d ago`;
}

function fmtDur(s) {
  if (!s || s <= 0) return '0s';
  const m = Math.floor(s / 60);
  const r = Math.round(s % 60);
  return m > 0 ? `${m}m ${r}s` : `${r}s`;
}

function initials(name) {
  return (name || '?').split(' ').filter(Boolean).map(w => w[0]).join('').toUpperCase().slice(0, 2);
}

function status(row) {
  if (row.visit_count >= 3 || row.total_time_seconds >= 600) return { label: 'HOT', bg: '#FF3B5C22', color: '#FF3B5C' };
  const last = new Date(row.last_visit);
  const first = new Date(row.first_visit);
  if (last.toDateString() === first.toDateString() && row.visit_count <= 1) return { label: 'NEW', bg: '#00D4C822', color: '#00D4C8' };
  return { label: 'RETURNING', bg: '#E8C54722', color: '#E8C547' };
}

function deepest(row) {
  if (!row.events || !row.events.length) return null;
  const time = {};
  row.events.forEach(e => {
    if (e.screen && e.duration_seconds > 0) time[e.screen] = (time[e.screen] || 0) + e.duration_seconds;
  });
  const sorted = Object.entries(time).sort((a, b) => b[1] - a[1]);
  return sorted[0] ? sorted[0][0] : null;
}

function toCsv(data) {
  const header = 'slug,contact_name,org_name,visit_count,total_time_seconds,last_visit,first_visit,screens_visited\n';
  const rows = data.map(r =>
    `${r.slug},${r.contact_name},${r.org_name},${r.visit_count},${r.total_time_seconds},${r.last_visit},${r.first_visit},"${(r.screens_visited || []).join(';')}"`
  ).join('\n');
  return header + rows;
}

export default function AdminActivity() {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [detail, setDetail] = useState(null);

  const token = localStorage.getItem('admin_token');

  const fetchData = useCallback(async () => {
    if (!token) { navigate('/admin/login'); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/activity`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.status === 401 || res.status === 403) { navigate('/admin/login'); return; }
      const json = await res.json();
      setData(json);
      setLastRefresh(new Date());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [token, navigate]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { const iv = setInterval(fetchData, 60000); return () => clearInterval(iv); }, [fetchData]);

  const fetchDetail = async (slug) => {
    if (expanded === slug) { setExpanded(null); setDetail(null); return; }
    setExpanded(slug);
    try {
      const res = await fetch(`${API}/admin/activity/${slug}`, { headers: { Authorization: `Bearer ${token}` } });
      const json = await res.json();
      setDetail(json);
    } catch (e) { setDetail(null); }
  };

  const handleExport = () => {
    const csv = toCsv(data);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `demo-activity-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  const now = Date.now();
  const sevenDays = 7 * 86400 * 1000;
  const recent = data.filter(r => new Date(r.last_visit).getTime() > now - sevenDays);
  const totalVisits = recent.reduce((s, r) => s + r.visit_count, 0);
  const activeCount = recent.length;
  const mostEngaged = data.length ? [...data].sort((a, b) => b.total_time_seconds - a.total_time_seconds)[0] : null;

  const C = { bg: '#0a0a0a', card: '#111', border: 'rgba(255,255,255,0.08)', gold: '#C9A84C', teal: '#00D4C8', white: '#F2F6FA', muted: '#888780', fog: '#6B7E91' };

  return (
    <div style={{ minHeight: '100vh', background: C.bg, color: C.white, fontFamily: "'Inter', sans-serif", padding: '32px 24px' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 32, flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h1 style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 36, margin: 0, letterSpacing: '0.04em' }}>Demo Activity</h1>
            <p style={{ fontFamily: "'Overpass Mono', monospace", fontSize: 11, color: C.muted, marginTop: 4 }}>
              {lastRefresh ? `Last refreshed ${relTime(lastRefresh.toISOString())} · auto-refreshes every 60s` : 'Loading...'}
            </p>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={fetchData} style={{ padding: '8px 16px', background: 'transparent', border: `1px solid ${C.border}`, color: C.muted, borderRadius: 8, fontSize: 12, cursor: 'pointer' }}>
              Refresh
            </button>
            <button onClick={handleExport} style={{ padding: '8px 16px', background: 'transparent', border: `1px solid ${C.gold}`, color: C.gold, borderRadius: 8, fontSize: 12, cursor: 'pointer' }}>
              Export CSV
            </button>
          </div>
        </div>

        {/* Summary strip */}
        <div style={{ display: 'flex', gap: 16, marginBottom: 32, flexWrap: 'wrap' }}>
          {[
            ['Visits (7d)', totalVisits],
            ['Active Demos (7d)', activeCount],
            ['Most Engaged', mostEngaged ? `${mostEngaged.contact_name || mostEngaged.slug} (${fmtDur(mostEngaged.total_time_seconds)})` : '—'],
          ].map(([l, v]) => (
            <div key={l} style={{ background: C.card, border: `1px solid ${C.border}`, borderLeft: `3px solid ${C.gold}`, borderRadius: 10, padding: '18px 22px', flex: 1, minWidth: 200 }}>
              <p style={{ fontFamily: "'Overpass Mono', monospace", fontSize: 10, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.12em', margin: '0 0 8px' }}>{l}</p>
              <p style={{ fontSize: 24, fontWeight: 700, color: C.gold, margin: 0 }}>{v}</p>
            </div>
          ))}
        </div>

        {/* Table */}
        {loading && !data.length ? (
          <p style={{ color: C.muted }}>Loading activity...</p>
        ) : !data.length ? (
          <p style={{ color: C.muted }}>No demo activity recorded yet.</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                  {['Contact', 'Last Visit', 'Visits', 'Screens', 'Time', 'Deepest', 'Status'].map(h => (
                    <th key={h} style={{ fontFamily: "'Overpass Mono', monospace", fontSize: 10, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.1em', textAlign: 'left', padding: '10px 8px', fontWeight: 600 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.map(row => {
                  const st = status(row);
                  const dp = deepest(row);
                  const isExpanded = expanded === row.slug;
                  return (
                    <React.Fragment key={row.slug}>
                      <tr onClick={() => fetchDetail(row.slug)} style={{ borderBottom: `1px solid ${C.border}`, cursor: 'pointer', background: isExpanded ? 'rgba(255,255,255,0.02)' : 'transparent' }}>
                        <td style={{ padding: '12px 8px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'rgba(0,212,200,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                              <span style={{ fontSize: 11, fontWeight: 700, color: C.teal }}>{initials(row.contact_name || row.org_name)}</span>
                            </div>
                            <div>
                              <p style={{ fontSize: 13, fontWeight: 600, color: C.white, margin: 0 }}>{row.contact_name || row.slug}</p>
                              <p style={{ fontSize: 11, color: C.fog, margin: 0 }}>{row.org_name}</p>
                            </div>
                          </div>
                        </td>
                        <td style={{ padding: '12px 8px', fontSize: 12, color: C.muted }}>{relTime(row.last_visit)}</td>
                        <td style={{ padding: '12px 8px', fontSize: 13, color: C.white, fontWeight: 600 }}>{row.visit_count}</td>
                        <td style={{ padding: '12px 8px' }}>
                          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                            {SCREENS.map(s => {
                              const visited = (row.screens_visited || []).includes(s);
                              return <span key={s} style={{ padding: '2px 6px', borderRadius: 4, fontSize: 9, fontWeight: 600, background: visited ? 'rgba(0,212,200,0.15)' : 'rgba(255,255,255,0.04)', color: visited ? C.teal : C.fog, letterSpacing: '0.04em' }}>{s.replace('_', ' ').slice(0, 6)}</span>;
                            })}
                          </div>
                        </td>
                        <td style={{ padding: '12px 8px', fontSize: 12, color: C.white, fontFamily: "'Overpass Mono', monospace" }}>{fmtDur(row.total_time_seconds)}</td>
                        <td style={{ padding: '12px 8px' }}>
                          {dp && <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 600, background: `${C.gold}18`, color: C.gold, border: `1px solid ${C.gold}40` }}>{dp.replace('_', ' ')}</span>}
                        </td>
                        <td style={{ padding: '12px 8px' }}>
                          <span style={{ padding: '3px 8px', borderRadius: 4, fontSize: 10, fontWeight: 700, background: st.bg, color: st.color, letterSpacing: '0.08em' }}>{st.label}</span>
                        </td>
                      </tr>
                      {isExpanded && detail && (
                        <tr>
                          <td colSpan={7} style={{ padding: '16px 8px 24px', background: 'rgba(255,255,255,0.01)' }}>
                            {/* Screen time bars */}
                            {detail.screen_time && Object.keys(detail.screen_time).length > 0 && (
                              <div style={{ marginBottom: 16 }}>
                                <p style={{ fontFamily: "'Overpass Mono', monospace", fontSize: 10, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8 }}>Screen Time</p>
                                {Object.entries(detail.screen_time).sort((a, b) => b[1] - a[1]).map(([screen, secs]) => {
                                  const maxSecs = Math.max(...Object.values(detail.screen_time));
                                  return (
                                    <div key={screen} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                                      <span style={{ fontSize: 11, color: C.muted, width: 80, flexShrink: 0 }}>{screen.replace('_', ' ')}</span>
                                      <div style={{ flex: 1, height: 6, background: 'rgba(255,255,255,0.04)', borderRadius: 3 }}>
                                        <div style={{ width: `${(secs / maxSecs) * 100}%`, height: '100%', background: C.teal, borderRadius: 3 }} />
                                      </div>
                                      <span style={{ fontFamily: "'Overpass Mono', monospace", fontSize: 10, color: C.muted, width: 50, textAlign: 'right' }}>{fmtDur(secs)}</span>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                            {/* Event timeline */}
                            <p style={{ fontFamily: "'Overpass Mono', monospace", fontSize: 10, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8 }}>Event Timeline ({detail.total_events} events)</p>
                            <div style={{ maxHeight: 300, overflowY: 'auto' }}>
                              {(detail.events || []).slice(0, 50).map((e, i) => (
                                <div key={i} style={{ display: 'flex', gap: 12, padding: '4px 0', borderBottom: `1px solid rgba(255,255,255,0.03)`, fontSize: 11 }}>
                                  <span style={{ fontFamily: "'Overpass Mono', monospace", color: C.fog, width: 130, flexShrink: 0 }}>{e.timestamp ? new Date(e.timestamp).toLocaleString() : ''}</span>
                                  <span style={{ color: C.teal, width: 120, flexShrink: 0 }}>{e.event}</span>
                                  <span style={{ color: C.muted }}>{e.screen || ''}{e.duration_seconds ? ` (${fmtDur(e.duration_seconds)})` : ''}</span>
                                </div>
                              ))}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
